/**
 * MOLR inertial smooth scroll
 * ---------------------------
 * Gives the desktop site a weighted, premium scroll feel using Lenis
 * (vendored in assets/molr-lenis.js — see its header for license).
 *
 * WHY THIS FILE IS CAREFUL — Horizon's scroll architecture is unusual:
 * on desktop (>= 990px) the window does NOT scroll. base.css sets
 * `html, body { overflow: hidden }` and the page scrolls inside
 * `.page-wrapper` (that's how the cart drawer "squeezes" the page).
 * So Lenis must be mounted on `.page-wrapper`, not on the window.
 *
 * Deliberate constraints (do not "simplify" these away):
 * 1. DESKTOP + MOUSE ONLY. On touch devices and viewports < 990px we keep
 *    100% native scrolling — inertial-scroll libraries fight the OS's own
 *    touch physics and hurt mobile performance. `(pointer: fine)` also
 *    excludes desktop touchscreens' primary input being touch.
 * 2. PROGRESSIVE ENHANCEMENT. If this file fails to load, everything
 *    still scrolls natively. Nothing here is load-bearing.
 * 3. REDUCED MOTION: users who ask for less motion get the browser's
 *    native scroll, untouched (media query below + live re-check).
 * 4. THEME EDITOR: skipped — the editor re-renders sections and its
 *    preview iframe behaves badly with hijacked wheel events.
 * 5. SCROLL LOCK: Horizon locks scrolling by putting a `scroll-lock`
 *    attribute on <html> when a modal drawer opens (see utilities.js
 *    lockScroll). A MutationObserver pauses/resumes Lenis in sync so the
 *    two systems never fight.
 * 6. NESTED SCROLLERS (cart items list, dropdowns, dialogs): handled two
 *    ways — `allowNestedScroll: true` lets Lenis detect any scrollable
 *    element under the cursor, and every <dialog> additionally gets the
 *    `data-lenis-prevent` attribute as a belt-and-braces guarantee.
 * 7. ANCHOR LINKS: Lenis's built-in `anchors` option only works when the
 *    wrapper is the window, so same-page anchors are handled manually
 *    below and eased through Lenis instead of hard-jumping.
 * 8. SHORT PAGES: if the content doesn't overflow the wrapper, Lenis's
 *    scroll limit is 0 and every input is a no-op — nothing to handle.
 */
(() => {
  'use strict';

  // --- Hard opt-outs (see header) -----------------------------------------
  if (document.documentElement.classList.contains('shopify-design-mode')) return;
  if (typeof globalThis.Lenis !== 'function') return; // vendor file missing/failed

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Desktop breakpoint MUST match base.css's 990px wrapper-scroll cutoff and
  // theme-drawer.js MODAL_BREAKPOINT. Below it, the window scrolls natively.
  const desktopMouse = window.matchMedia('(min-width: 990px) and (pointer: fine)');

  /** @type {any} The live Lenis instance, or null when native scroll is active. */
  let lenis = null;

  /** Pauses Lenis whenever Horizon sets <html scroll-lock> (drawer open). */
  const scrollLockObserver = new MutationObserver(() => {
    if (!lenis) return;
    if (document.documentElement.hasAttribute('scroll-lock')) {
      lenis.stop();
    } else {
      lenis.start();
    }
  });

  function start() {
    if (lenis) return;

    const wrapper = document.querySelector('.page-wrapper');
    if (!wrapper) return; // e.g. password page — no wrapper, nothing to do

    // Mark dialogs (cart drawer, menu drawer, search, zoom…) so wheel events
    // inside them always scroll their own content, never the page behind.
    document.querySelectorAll('dialog').forEach((dialog) => {
      dialog.setAttribute('data-lenis-prevent', '');
    });

    lenis = new globalThis.Lenis({
      wrapper,
      content: wrapper, // Lenis reads content.scrollHeight — the wrapper itself measures correctly
      autoRaf: true, // Lenis runs its own requestAnimationFrame loop
      lerp: 0.09, // slightly heavier than default 0.1 — "weighted", never floaty
      wheelMultiplier: 1,
      smoothWheel: true,
      syncTouch: false, // never hijack touch — belt-and-braces with the media query
      allowNestedScroll: true, // inner scrollable areas keep native wheel behaviour
    });

    // If a drawer is already open when we start (e.g. restored state), sync.
    if (document.documentElement.hasAttribute('scroll-lock')) lenis.stop();

    scrollLockObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['scroll-lock'],
    });
  }

  function stop() {
    if (!lenis) return;
    scrollLockObserver.disconnect();
    lenis.destroy(); // removes listeners + the lenis classes from .page-wrapper
    lenis = null;
  }

  function sync() {
    // Native scroll for reduced-motion users and any non-desktop-mouse context
    if (reducedMotion.matches || !desktopMouse.matches) {
      stop();
    } else {
      start();
    }
  }

  // React live to viewport resizes across 990px and to OS motion-pref changes
  desktopMouse.addEventListener('change', sync);
  reducedMotion.addEventListener('change', sync);
  sync();

  // --- Smooth same-page anchor links (see constraint 7) ---------------------
  // Capture-phase so we run before any other handler can scroll natively.
  document.addEventListener(
    'click',
    (event) => {
      if (!lenis) return; // native scroll active — let the browser handle it
      const link = event.target instanceof Element && event.target.closest('a[href*="#"]');
      if (!link) return;

      // Only same-page anchors; leave cross-page URLs, downloads, new tabs alone
      const url = new URL(link.href, location.href);
      if (url.pathname !== location.pathname || url.search !== location.search) return;
      if (!url.hash || url.hash === '#') return;

      let target = null;
      try {
        target = document.querySelector(url.hash);
      } catch {
        return; // hash isn't a valid selector (e.g. "#/state") — not an anchor
      }
      if (!target) return;

      event.preventDefault();
      history.pushState(null, '', url.hash); // keep the URL fragment in sync, like native
      lenis.scrollTo(target, { duration: 1.1 });

      // Native fragment navigation also moves focus to the target; because we
      // prevented it, do that part by hand. Without this a keyboard user's
      // focus stays behind where the page just took them — which matters most
      // for "skip past this" links like the pen hero's scroll cue.
      // preventScroll keeps the focus call from fighting the eased scroll.
      if (target instanceof HTMLElement) {
        target.focus({ preventScroll: true });
      }
    },
    true
  );

  // Exposed for debugging in DevTools (`molrLenis.velocity`, etc.) — optional.
  Object.defineProperty(window, 'molrLenis', { get: () => lenis });
})();
